import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarDays,
  RefreshCw,
  Download,
  ClipboardList,
  Search,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Star,
  Layers,
  Folder, Users, Maximize2, Tag, Box 
} from "lucide-react";
import * as XLSX from 'xlsx';
// 1. TAMBAHKAN IMPORT BERIKUT DI BARIS ATAS (bersama import lainnya)
import { BarChart,AreaChart,Area, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
// Varian animasi global untuk konsistensi
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.04 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
};

const tableRowVariants = {
  hidden: { opacity: 0, x: -5 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.02, ease: "easeOut" }
  })
};

export default function StockCard() {
  // =========================
  // 1. STATE
  // =========================
  const [loading, setLoading] = useState(false);  
  const [tanggalStok, setTanggalStok] = useState("");
  const [lastAudit, setLastAudit] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [detailArtikel, setDetailArtikel] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState("summary");
  const [today, setToday] = useState({});
  const [activeMatrixTab, setActiveMatrixTab] = useState("dept"); 

  const [showChartModal, setShowChartModal] = useState(false);
  const [activeRatingComments, setActiveRatingComments] = useState(null); // Menyimpan item.id atau item.artikel

  const [summary, setSummary] = useState({
    stok_audit: 0,
    sj_masuk: 0,
    mutasi_masuk: 0,
    pbm_rebarcode: 0,
    tukar_size_in: 0,
    tukar_size_out: 0,
    ret_whs: 0,
    ret_promosi: 0,
    ret_rebarcode: 0,
    penjualan: 0,
    mutasi_keluar: 0,
    stok_akhir: 0,
    order_tiktok: 0,
    penjualan_kyw: 0,
    cancel_order_tiktok:0,
    cancel_order_out:0
  });


  const handleCardExportBroken = (type) => {
  if (type === "broken-size") {
    // Filter langsung dari core data tabel rincian artikel Anda
    const allBrokenSizeArticles = filteredArtikel.filter((item) => {
      const extCode = item.kode_extend || item.artikel;
      const uniqueSizesWithStock = brokenSizeMap[extCode] ? brokenSizeMap[extCode].size : 0;
      const sAkhir = Number(item.stok_akhir || 0);
      
      return sAkhir > 0 && uniqueSizesWithStock === 1 && item.ukuran !== "FR";
    });

    if (allBrokenSizeArticles.length === 0) {
      alert("Tidak ada artikel berstatus Broken Size untuk diekspor.");
      return;
    }

    // Mapping kolom persis seperti layout tabel operasional retail Anda
    const excelRows = allBrokenSizeArticles.map((item, index) => {
      const totalIn = Number(item.sj_masuk || 0) + Number(item.mutasi_masuk || 0) + Number(item.pbm_rebarcode || 0) + Number(item.tukar_size_in || 0);
      const totalOut = Number(item.penjualan || 0) + Number(item.penjualan_kyw || 0) + Number(item.mutasi_keluar || 0) + Number(item.ret_rebarcode || 0) + Number(item.tukar_size_out || 0) + Number(item.ret_promosi || 0) + Number(item.ret_whs || 0) + Number(item.order_tiktok || 0);
      
      return {
        "No": index + 1,
        "Artikel": item.artikel,
        "Barcode": item.barcode,
        "Departemen": item.dept,
        "Prinsipal / Brand": item.prinsipal,
        "Gender": item.gender || "-",
        "Ukuran Sisa": item.ukuran,
        "Stok Audit": Number(item.stok_audit || 0),
        "Total Masuk": totalIn,
        "Total Keluar": totalOut,
        "Stok Awal": Number(item.stok_audit || 0) + totalIn - totalOut,
        "Stok Akhir Kritis": Number(item.stok_akhir || 0),
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(excelRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Daftar Artikel Broken Size");

    XLSX.writeFile(workbook, `Detail_Artikel_Broken_Size_${new Date().toISOString().split('T')[0]}.xlsx`);
  }
};
  // =========================
  // 2. FETCH API
  // =========================
  const getInventory = async () => {
    try {
      setLoading(true);
      const kode_toko = localStorage.getItem("kode_toko");
      const res = await axios.get("/api/inventory/stock-card", {
        params: { kode_toko: kode_toko },
      });

      const data = res.data;
      setToday(data.today || {});
      setLastAudit(data.last_audit || null);
      setDetailArtikel(data.detail || data.detail_artikel || []);
      setSummary({
        stok_audit: data.summary?.stok_audit || 0,
        sj_masuk: data.summary?.sj_masuk || 0,
        mutasi_masuk: data.summary?.mutasi_masuk || 0,
        pbm_rebarcode: data.summary?.pbm_rebarcode || 0,
        tukar_size_in: data.summary?.tukar_size_in || 0,
        tukar_size_out: data.summary?.tukar_size_out || 0,
        ret_whs: data.summary?.ret_whs || 0,
        ret_promosi: data.summary?.ret_promosi || 0,
        ret_rebarcode: data.summary?.ret_rebarcode || 0,
        penjualan: data.summary?.penjualan || 0,
        mutasi_keluar: data.summary?.mutasi_keluar || 0,
        order_tiktok: data.summary?.order_tiktok || 0,
        penjualan_kyw: data.summary?.penjualan_kyw || 0,
        stok_akhir: data.summary?.stok_akhir || 0,
        cancel_order_tiktok:data.summary?.cancel_order_tiktok || 0,
        cancel_order_out:data.summary?.cancel_order_out || 0
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getInventory();
  }, []);

  // ===================================================
  // 3. LOGIC FILTER DATA (Harus Paling Atas di Antara Hooks)
  // ===================================================
  const filteredArtikel = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return detailArtikel;

    return detailArtikel.filter((item) => {
      return (
        item.artikel?.toLowerCase().includes(query) ||
        item.barcode?.toLowerCase().includes(query) ||
        item.dept?.toLowerCase().includes(query) ||
        item.prinsipal?.toLowerCase().includes(query)
      );
    });
  }, [detailArtikel, searchQuery]);

  // ===================================================
  // 4. DATA MATRIX GENERATOR & BROKEN SIZE MAP
  // ===================================================
const brokenSizeMap = useMemo(() => {
    const map = {};
    const sourceData = filteredArtikel || [];

    sourceData.forEach((item) => {
      const extCode = item.kode_extend || item.artikel;
      const stokAkhir = Number(item.stok_akhir || 0);

      // KONDISI BARU: Stok aktif, DAN ukurannya BUKAN "FR"
      if (stokAkhir > 0 && item.ukuran !== "FR") { 
        if (!map[extCode]) {
          map[extCode] = new Set();
        }
        map[extCode].add(item.ukuran);
      }
    });

    return map;
  }, [filteredArtikel]);

  // ========================================================
  // WIDGET BARU: KHUSUS MENGHITUNG MATRIX UNTUK BROKEN SIZE
  // ========================================================
  const brokenSizeMatricesData = useMemo(() => {
    const dimensions = {
      dept: {},
      gender: {},
      size: {},
      brand: {},
      modul: {},
    };

    filteredArtikel.forEach((item) => {
      const extCode = item.kode_extend || item.artikel;
      const uniqueSizesWithStock = brokenSizeMap[extCode] ? brokenSizeMap[extCode].size : 0;
      const sAkhir = Number(item.stok_akhir || 0);

      // Kriteria filter ketat: Hanya ambil yang berstatus Broken Size (Bukan "FR")
      const isBrokenSize = sAkhir > 0 && uniqueSizesWithStock === 1 && item.ukuran !== "FR";
      if (!isBrokenSize) return;

      const dKey = item.dept || "Unknown Dept";
      const gKey = item.gender || "Unknown Gender";
      const sKey = item.ukuran || "Unknown Size";
      const bKey = item.prinsipal || "Unknown Brand";
      const mKey = item.modul || "Unknown Modul";

      const sAudit = Number(item.stok_audit || 0);
      const tIn = Number(item.sj_masuk || 0) + Number(item.mutasi_masuk || 0) + Number(item.pbm_rebarcode || 0) + Number(item.tukar_size_in || 0);
      const tOut = Number(item.penjualan || 0) + Number(item.penjualan_kyw || 0) + Number(item.mutasi_keluar || 0) + Number(item.ret_rebarcode || 0) + Number(item.tukar_size_out || 0) + Number(item.ret_promosi || 0) + Number(item.ret_whs || 0) + Number(item.order_tiktok || 0);

      const accumulate = (dim, key) => {
        if (!dimensions[dim][key]) {
          dimensions[dim][key] = { label: key, stokAudit: 0, totalIn: 0, totalOut: 0, stokAkhir: 0 };
        }
        dimensions[dim][key].stokAudit += sAudit;
        dimensions[dim][key].totalIn += tIn;
        dimensions[dim][key].totalOut += tOut;
        dimensions[dim][key].stokAkhir += sAkhir;
      };

      accumulate("dept", dKey);
      accumulate("gender", gKey);
      accumulate("size", sKey);
      accumulate("brand", bKey);
      accumulate("modul", mKey);
    });

    return {
      dept: Object.values(dimensions.dept),
      gender: Object.values(dimensions.gender),
      size: Object.values(dimensions.size),
      brand: Object.values(dimensions.brand),
      modul: Object.values(dimensions.modul),
    };
  }, [filteredArtikel, brokenSizeMap]);

  const matricesData = useMemo(() => {
    const dimensions = {
      dept: {},
      gender: {},
      size: {},
      brand: {},
      modul: {},
    };

    filteredArtikel.forEach((item) => {
      const dKey = item.dept || "Unknown Dept";
      const gKey = item.gender || "Unknown Gender";
      const sKey = item.ukuran || "Unknown Size";
      const bKey = item.prinsipal || "Unknown Brand";
      const mKey = item.modul || "Unknown Modul";

      const sAudit = Number(item.stok_audit || 0);
      const tIn = Number(item.sj_masuk || 0) + Number(item.mutasi_masuk || 0) + Number(item.pbm_rebarcode || 0) + Number(item.tukar_size_in || 0) + Number(item.cancel_order_tiktok || 0);
      const tOut = Number(item.penjualan || 0) + Number(item.penjualan_kyw || 0) + Number(item.mutasi_keluar || 0) + Number(item.ret_rebarcode || 0) + Number(item.tukar_size_out || 0) + Number(item.ret_promosi || 0) + Number(item.ret_whs || 0) + Number(item.order_tiktok || 0);
      const sAkhir = Number(item.stok_akhir || 0);

      const accumulate = (dim, key) => {
        if (!dimensions[dim][key]) {
          dimensions[dim][key] = { label: key, stokAudit: 0, totalIn: 0, totalOut: 0, stokAkhir: 0 };
        }
        dimensions[dim][key].stokAudit += sAudit;
        dimensions[dim][key].totalIn += tIn;
        dimensions[dim][key].totalOut += tOut;
        dimensions[dim][key].stokAkhir += sAkhir;
      };

      accumulate("dept", dKey);
      accumulate("gender", gKey);
      accumulate("size", sKey);
      accumulate("brand", bKey);
      accumulate("modul", mKey);
    });

    return {
      dept: Object.values(dimensions.dept),
      gender: Object.values(dimensions.gender),
      size: Object.values(dimensions.size),
      brand: Object.values(dimensions.brand),
      modul: Object.values(dimensions.modul),
    };
  }, [filteredArtikel]);

  // ===================================================
  // 5. PAGINATION UTK TABEL DETAIL
  // ===================================================
  const paginatedData = useMemo(() => {
    const offset = (currentPage - 1) * itemsPerPage;
    return filteredArtikel.slice(offset, offset + itemsPerPage);
  }, [filteredArtikel, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredArtikel.length / itemsPerPage);

  // ===================================================
  // 6. FORMAT & UTILS STATS
  // ===================================================
  const formattedLastAuditDate = useMemo(() => {
    if (!lastAudit || !lastAudit.tanggal) return "Belum Pernah Audit";
    return new Date(lastAudit.tanggal).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }, [lastAudit]);

  const handleCardExport = (type) => {
    if (!type) return;
    const kode_toko = localStorage.getItem("kode_toko");
    const dateFromFormatted = lastAudit?.tanggal 
      ? new Date(lastAudit.tanggal).toISOString().split('T')[0] 
      : '2026-05-01';

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const dateToFormatted = yesterday.toISOString().split('T')[0];

    window.open(
      `/api/inventory/export-${type}?kode_toko=${kode_toko}&dateFrom=${dateFromFormatted}&dateTo=${dateToFormatted}`,
      "_blank"
    );
  };

  const summaryStats = [
    { label: "Stok Audit", value: summary.stok_audit.toLocaleString("id-ID"), color: "from-slate-700 to-slate-900", type: null },
    { label: "SJ Masuk", value: summary.sj_masuk.toLocaleString("id-ID"), color: "from-green-500 to-emerald-500", type: "sj-masuk" },
    { label: "Mutasi Masuk", value: summary.mutasi_masuk.toLocaleString("id-ID"), color: "from-green-500 to-emerald-500", type: "mutasi-masuk" },
    { label: "PBM Rebarcode", value: summary.pbm_rebarcode.toLocaleString("id-ID"), color: "from-green-500 to-emerald-500", type: "pbm-rebarcode" },
    { label: "Cancel Order", value: summary.cancel_order_tiktok.toLocaleString("id-ID"), color: "from-green-500 to-emerald-500", type: "cancel-order" },
    { label: "Tukar Size In", value: summary.tukar_size_in.toLocaleString("id-ID"), color: "from-green-500 to-emerald-500", type: "tukar-size-in" },
    { label: "Penjualan", value: summary.penjualan.toLocaleString("id-ID"), color: "from-red-500 to-rose-500", type: "penjualan" },
    { label: "Keeping Order", value: summary.order_tiktok.toLocaleString("id-ID"), color: "from-red-500 to-rose-500", type: "order-tiktok" },
    { label: "Penjualan Kyw", value: summary.penjualan_kyw.toLocaleString("id-ID"), color: "from-red-500 to-rose-500", type: "penjualan-kyw" },
    { label: "Mutasi Keluar", value: summary.mutasi_keluar.toLocaleString("id-ID"), color: "from-red-500 to-rose-500", type: "mutasi-keluar" },
    { label: "Return WHS", value: summary.ret_whs.toLocaleString("id-ID"), color: "from-red-500 to-rose-500", type: "ret-whs" },
    { label: "Return Promosi", value: summary.ret_promosi.toLocaleString("id-ID"), color: "from-red-500 to-rose-500", type: "ret-promosi" },
    { label: "PBK Rebarcode", value: summary.ret_rebarcode.toLocaleString("id-ID"), color: "from-red-500 to-rose-500", type: "ret-rebarcode" },
    { label: "Tukar Size Out", value: summary.tukar_size_out.toLocaleString("id-ID"), color: "from-red-500 to-rose-500", type: "tukar-size-out" },
   { label: "Cancel Order Out", value: summary.cancel_order_out.toLocaleString("id-ID"), color: "from-red-500 to-rose-500", type: "cancel-order-out" },
    { label: "Stok Akhir", value: summary.stok_akhir.toLocaleString("id-ID"), color: "from-slate-700 to-slate-900", type: null },
  ];

  const todayStats = [
    { label: "Stok Awal", value: summary.stok_akhir.toLocaleString("id-ID"), color: "from-slate-700 to-slate-900" },
    { label: "SJ Hari Ini", value: today.sj_today?.toLocaleString("id-ID") || "0", color: "from-green-500 to-emerald-500" },
    { label: "Mutasi Masuk", value: today.mutasi_masuk_today?.toLocaleString("id-ID") || "0", color: "from-green-500 to-emerald-500" },
    { label: "Req Order Tiktok", value: today.mutasi_by_order_today?.toLocaleString("id-ID") || "0", color: "from-green-500 to-emerald-500" },
    { label: "Cancel Order", value: today.cancel_order_today?.toLocaleString("id-ID") || "0", color: "from-green-500 to-emerald-500" },
    { label: "Penjualan", value: today.penjualan_today?.toLocaleString("id-ID") || "0", color: "from-red-500 to-rose-500" },
    { label: "Mutasi Keluar", value: today.mutasi_keluar_today?.toLocaleString("id-ID") || "0", color: "from-red-500 to-rose-500" },
    { label: "Req Order Tiktok", value: today.req_keluar_today?.toLocaleString("id-ID") || "0", color: "from-red-500 to-rose-500" },
    { 
      label: "Shipping Stock", 
      value: ((Number(today?.pick_pack_stock) || 0) + (Number(today?.mutasi_by_order_today) || 0)).toLocaleString("id-ID"), 
      color: "from-red-500 to-rose-500" 
    },
    { label: "Penjualan Kyw", value: today.penjualan_kyw_today?.toLocaleString("id-ID") || "0", color: "from-red-500 to-rose-500" },
  ];

  const handleExport = async () => {
    const kode_toko = localStorage.getItem("kode_toko");
    window.open(`/api/inventory/export-stock-card?kode_toko=${kode_toko}&dateFrom=${dateFrom}&dateTo=${dateTo}`, "_blank");
  };

  // ===================================================
  // 7. RETURN JSX (Silakan lanjutkan dengan return() komponen Anda di bawah)
  // ===================================================

  return (
    <div className="p-6 min-h-screen bg-slate-50/50 space-y-6">
      
      {/* HEADER SECTION */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Stock Card</h1>
            <p className="text-sm text-slate-500 mt-1">
              Monitor stok, distribusi, replenishment & performa inventory
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="h-[45px] border border-slate-200 rounded-2xl px-4 flex items-center gap-2 bg-white shadow-sm">
              <CalendarDays size={16} className="text-slate-400" />
              <input
                type="date"
                value={tanggalStok}
                onChange={(e) => setTanggalStok(e.target.value)}
                className="text-sm font-medium outline-none bg-transparent text-slate-700"
              />
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={getInventory}
              disabled={loading}
              className="h-[45px] px-4 rounded-2xl border border-slate-200 hover:bg-slate-50 flex items-center gap-2 bg-white text-slate-700 font-medium text-sm shadow-sm"
            >
              <RefreshCw size={16} className={`${loading ? "animate-spin text-indigo-600" : "text-slate-400"}`} />
              Refresh
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleExport}
              className="h-[45px] px-5 rounded-2xl bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-medium text-sm flex items-center gap-2 shadow-sm hover:shadow-indigo-100"
            >
              <Download size={16} />
              Export Global
            </motion.button>
          </div>
        </div>

        <div className="h-px bg-slate-100 my-6" />

        {/* CONTROLS & TOGGLE VIEW MODE */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <div>
            <h3 className="font-semibold text-slate-800 text-sm">Rangkuman Mutasi</h3>
            <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-1">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Tanggal Terakhir Audit: <strong className="text-slate-700 font-semibold">{formattedLastAuditDate}</strong>
            </p>
          </div>

          <div className="bg-slate-100 rounded-2xl p-1 flex self-start sm:self-center">
            {["summary", "today"].map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`relative px-4 py-2 text-xs font-semibold rounded-xl transition-all duration-200 ${
                  viewMode === mode
                    ? "bg-white text-indigo-600 shadow-sm font-bold"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {mode === "summary" ? "Stock Position" : "Today Activity"}
              </button>
            ))}
          </div>
        </div>

        {/* STATS CARDS GRID ANIMATION */}
        <div className="min-h-[110px]">
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div 
                key="skeleton"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-12 gap-3"
              >
                {Array.from({ length: viewMode === "summary" ? 14 : 10 }).map((_, idx) => (
                  <div key={idx} className="bg-slate-50 border border-slate-100 rounded-2xl p-4 animate-pulse">
                    <div className="w-8 h-1 bg-slate-200 rounded-full mb-3" />
                    <div className="h-3 bg-slate-200 rounded w-2/3 mb-2" />
                    <div className="h-5 bg-slate-300 rounded w-1/2" />
                  </div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key={viewMode}
                variants={containerVariants}
                initial="hidden"
                animate="show"
                exit="hidden"
                className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-12 gap-3"
              >
                {(viewMode === "summary" ? summaryStats : todayStats).map((item, i) => {
                  const isClickable = !!item.type;
                  return (
                    <motion.div
                      key={i}
                      variants={itemVariants}
                      whileHover={isClickable ? { y: -4, shadow: "0 10px 15px -3px rgba(0,0,0,0.05)" } : {}}
                      whileTap={isClickable ? { scale: 0.97 } : {}}
                      onClick={() => handleCardExport(item.type)}
                      className={`rounded-2xl border bg-slate-50/70 p-4 transition-colors duration-150 relative overflow-hidden ${
                        isClickable 
                          ? "cursor-pointer border-slate-200/60 bg-white hover:border-indigo-100 hover:bg-gradient-to-b hover:from-white hover:to-indigo-50/20" 
                          : "border-slate-100"
                      }`}
                    >
                      <div className={`w-8 h-1 rounded-full mb-3 bg-gradient-to-r ${item.color}`} />
                      <p className="text-[11px] text-slate-500 font-medium tracking-wide truncate">{item.label}</p>
                      <h2 className="mt-1.5 text-base font-bold text-slate-800 tracking-tight">{item.value}</h2>
                      {isClickable && (
                        <div className="absolute right-2 bottom-2 opacity-0 hover:opacity-100 transition-opacity">
                          <span className="text-[9px] font-bold text-indigo-500 uppercase px-1 bg-indigo-50 rounded">XLS</span>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* SECTION MATRIX STOK */}

{/* SECTION MATRIX STOK & GRAFIK */}
{/* ========================================== */}
{/* 5 CARD MATRIX GRID DENGAN GRAFIK MANDIRI */}
{/* ========================================== */}
<div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
  {[
    { id: "dept", name: "Kategori Dept", icon: <Folder size={16} className="text-blue-500" />, color: "#3b82f6" },
    { id: "gender", name: "Gender", icon: <Users size={16} className="text-purple-500" />, color: "#a855f7" },
    { id: "size", name: "Size / Ukuran", icon: <Maximize2 size={16} className="text-amber-500" />, color: "#f59e0b" },
    { id: "brand", name: "Brand (Prinsipal)", icon: <Tag size={16} className="text-rose-500" />, color: "#f43f5e" },
    { id: "modul", name: "Modul Distribusi", icon: <Box size={16} className="text-emerald-500" />, color: "#10b981" }
  ].map((matrix) => {
    const chartData = matricesData[matrix.id] || [];
    
    // Hitung akumulasi ringkas untuk header mini-card
    const totalInAll = chartData.reduce((acc, curr) => acc + curr.totalIn + curr.stokAudit, 0);
    const totalOutAll = chartData.reduce((acc, curr) => acc + curr.totalOut, 0);
    const stokAkhirAll = chartData.reduce((acc, curr) => acc + curr.stokAkhir, 0);

    return (
      <div key={matrix.id} className="bg-white rounded-3xl border border-slate-200 shadow-sm p-4 flex flex-col justify-between hover:border-slate-300 transition-all duration-200">
        
        {/* Header Card */}
        <div>
          <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
            <div className="flex items-center gap-1.5">
              {matrix.icon}
              <h4 className="text-xs font-bold text-slate-800 tracking-tight">{matrix.name}</h4>
            </div>
            <span className="text-[10px] font-bold text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded-md">
              {chartData.length} Item
            </span>
          </div>

          {/* Mini Indikator Angka */}
          <div className="grid grid-cols-3 gap-1 text-center my-3 bg-slate-50/70 p-2 rounded-xl border border-slate-100/50">
            <div>
              <p className="text-[9px] text-slate-400 font-semibold uppercase">In</p>
              <p className="text-[11px] font-bold text-emerald-600 font-mono mt-0.5">
                {totalInAll > 0 ? totalInAll.toLocaleString("id-ID") : "0"}
              </p>
            </div>
            <div className="border-x border-slate-200/60">
              <p className="text-[9px] text-slate-400 font-semibold uppercase">Out</p>
              <p className="text-[11px] font-bold text-rose-500 font-mono mt-0.5">
                {totalOutAll > 0 ? totalOutAll.toLocaleString("id-ID") : "0"}
              </p>
            </div>
            <div>
              <p className="text-[9px] text-slate-400 font-semibold uppercase">Stok</p>
              <p className="text-[11px] font-bold text-slate-900 font-mono mt-0.5">
                {stokAkhirAll.toLocaleString("id-ID")}
              </p>
            </div>
          </div>
        </div>

        {/* Grafik Area Multi-Line didalam Card */}
        <div className="w-full h-32 mt-1">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
                <XAxis 
                  dataKey="label" 
                  hide={true} 
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    fontSize: "10px",
                    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)"
                  }}
                  itemStyle={{ padding: "1px 0" }}
                  // Format label dan value di dalam tooltip
                  formatter={(value, name, props) => {
                    const formattedValue = Number(value).toLocaleString("id-ID");
                    
                    if (name === "Terjual") {
                      // Hitung kontribusi persen dari totalOut item ini dibanding totalOutAll card
                      const persentase = totalOutAll > 0 
                        ? ((Number(value) / totalOutAll) * 100).toFixed(1) 
                        : 0;
                      return [`${formattedValue} (${persentase}%)`, name];
                    }
                    
                    return [formattedValue, name];
                  }}
                />
                {/* Area Total In */}
                <Area
                  type="monotone"
                  name="Total In"
                  dataKey="totalIn"
                  stroke="#10b981"
                  fillOpacity={0.04}
                  fill="#10b981"
                  strokeWidth={1.5}
                />
                {/* Area Terjual (Total Out) */}
                <Area
                  type="monotone"
                  name="Terjual"
                  dataKey="totalOut"
                  stroke="#f43f5e"
                  fillOpacity={0.04}
                  fill="#f43f5e"
                  strokeWidth={1.5}
                />
                {/* Area Stok Akhir */}
                <Area
                  type="monotone"
                  name="Stok Akhir"
                  dataKey="stokAkhir"
                  stroke={matrix.color}
                  fillOpacity={0.1}
                  fill={matrix.color}
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-300 text-[10px]">
              Tidak ada data pergerakan
            </div>
          )}
        </div>

        {/* Legend Ringkas Bawah Grafik */}
        <div className="flex items-center justify-center gap-3 border-t border-slate-50 pt-2 mt-2 text-[9px] font-bold text-slate-400">
          <div className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> In
          </div>
          <div className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500" /> Out
          </div>
          <div className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: matrix.color }} /> Stok
          </div>
        </div>

      </div>
    );
  })}
</div>

      {/* TABLE SECTION LIST DETAIL ARTIKEL */}
      {/* TABLE SECTION LIST DETAIL ARTIKEL */}
<div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden relative">
  
  {/* Panel Header & Pencarian */}
  <div className="p-5 bg-slate-50/70 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
    <div className="flex items-center gap-2">
      <ClipboardList size={18} className="text-indigo-600" />
      <h3 className="text-sm font-semibold text-slate-800">Rincian Pergerakan Per Artikel</h3>
    </div>
    
    <div className="relative w-full sm:w-72">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
      <input
        type="text"
        placeholder="Cari Artikel, Barcode, Dept..."
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          setCurrentPage(1);
        }}
        className="w-full h-9 pl-9 pr-4 text-xs border border-slate-200 rounded-xl outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-white shadow-sm transition-all"
      />
    </div>
  </div>

  {/* Panel Tabel Grid Konten */}
  <div className="overflow-x-auto">
    <table className="w-full text-left border-collapse text-xs whitespace-nowrap">
      <thead>
        <tr className="bg-slate-100/80 text-slate-600 font-semibold border-b border-slate-200 uppercase text-[10px]">
          <th className="p-3 pl-5 sticky left-0 bg-slate-100 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] min-w-[200px] z-10">Artikel / Barcode</th>
          {/* KOLOM RATING ARTIKEL */}
          <th className="p-3 text-center">Rating Performa</th>
          <th className="p-3">Dept</th>
          <th className="p-3">Prinsipal</th>
          <th className="p-3">Gender / Size</th>
          <th className="p-3 text-center">Status Matrix</th>
          
          {/* Kolom Indikator Utama */}
          <th className="p-3 text-center bg-slate-50 border-x border-slate-200/60">Stok Audit</th>
          <th className="p-3 text-center bg-emerald-50 text-emerald-700 border-r border-slate-200/60">Total In</th>
          <th className="p-3 text-center bg-rose-50 text-rose-700 border-r border-slate-200/60">Total Out</th>
          <th className="p-3 text-center bg-indigo-50 text-indigo-700 border-r border-slate-200">Stok Awal</th>
          
          {/* Kolom Aktivitas Hari Ini (Today) */}
          <th className="p-3 text-center bg-emerald-100/50 text-emerald-800 border-r border-slate-200/60">In Today</th>
          <th className="p-3 text-center bg-rose-100/50 text-rose-800 border-r border-slate-200/60">Out Today</th>
          
          <th className="p-3 text-center bg-violet-600 text-white shadow-md">Stok Akhir</th>
        </tr>
      </thead>
      
      <tbody className="divide-y divide-slate-100">
        <AnimatePresence mode="popLayout">
          {paginatedData.length > 0 ? (
            paginatedData.map((item, index) => {
              const totalIn = Number(item.sj_masuk || 0) + Number(item.mutasi_masuk || 0) + Number(item.pbm_rebarcode || 0) + Number(item.tukar_size_in || 0);
              const totalOut = Number(item.penjualan || 0) + Number(item.penjualan_kyw || 0) + Number(item.mutasi_keluar || 0) + Number(item.ret_rebarcode || 0) + Number(item.tukar_size_out || 0) + Number(item.ret_promosi || 0) + Number(item.ret_whs || 0) + Number(item.order_tiktok || 0);
              const stokAwal = Number(item.stok_audit || 0) + totalIn - totalOut;
              
              const totalInToday = Number(item.sj_today || 0) + Number(item.mutasi_masuk_today || 0) + Number(item.mutasi_by_order_today || 0) + Number(item.cancel_order_today || 0);
              const totalOutToday = Number(item.penjualan_today || 0) + Number(item.penjualan_kyw_today || 0) + Number(item.mutasi_keluar_today || 0) + Number(item.req_keluar_today || 0) + Number(item.pick_pack_stock || 0);

              // LOGIKA BROKEN SIZE EVALUATION
              const extCode = item.kode_extend || item.artikel;
              const uniqueSizesWithStock = brokenSizeMap[extCode] ? brokenSizeMap[extCode].size : 0;
              const isStokAktif = Number(item.stok_akhir || 0) > 0;
              const isBrokenSize = isStokAktif && uniqueSizesWithStock === 1 && item.ukuran !== "FR";

              // FUNGSI SAMPEL STATIS UNTUK RATING BINTANG MAKSIMAL 5
              const getStaticRating = (stok) => {
                if (stok === 0) return 1;
                if (stok <= 2) return 2;
                if (stok <= 5) return 3;
                if (stok <= 15) return 4;
                return 5;
              };
              const currentRating = getStaticRating(Number(item.stok_akhir || 0));

              // DATA SAMPEL KOMENTAR STATIS BERDASARKAN RATING
              const getStaticComments = (rating) => {
                const commentMap = {
                  1: [
                    { user: "Andi M.", text: "Bahan agak panas, jahitan kurang rapi.", date: "02 Jun" },
                    { user: "Siti K.", text: "Ukuran tidak sesuai dengan deskripsi.", date: "29 Mei" }
                  ],
                  2: [
                    { user: "Rian H.", text: "Warna cepat pudar setelah dicuci sekali.", date: "04 Jun" },
                    { user: "Dewi A.", text: "Model bagus tapi kainnya tipis.", date: "01 Jun" }
                  ],
                  3: [
                    { user: "Budi S.", text: "Standar, sesuai dengan harga produk.", date: "05 Jun" },
                    { user: "Mega UT.", text: "Bahan oke, tapi pengiriman agak lambat.", date: "03 Jun" }
                  ],
                  4: [
                    { user: "Eko P.", text: "Bahan adem, nyaman dipakai seharian.", date: "06 Jun" },
                    { user: "Lina W.", text: "Kualitas jahitan kuat, respon seller cepat.", date: "04 Jun" }
                  ],
                  5: [
                    { user: "Ahmad F.", text: "Sangat puas! Bahan premium dan tebal.", date: "07 Jun" },
                    { user: "Putri R.", text: "Langganan tetap, kualitas selalu terjaga.", date: "06 Jun" }
                  ]
                };
                return commentMap[rating] || [];
              };
              const sampleComments = getStaticComments(currentRating);

              return (
                <motion.tr 
                  custom={index}
                  variants={tableRowVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  key={item.id || item.barcode || index} 
                  className={`hover:bg-indigo-50/20 transition-colors text-slate-700 ${isBrokenSize ? "bg-amber-50/30" : ""}`}
                >
                  {/* KOLOM ARTIKEL / BARCODE + FOTO */}
                  <td className="p-3 pl-5 sticky left-0 bg-white shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] z-10">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg overflow-hidden border border-slate-100 bg-slate-50 flex-shrink-0 flex items-center justify-center shadow-sm relative">
                        <img
                          src={`https://asset-3s.3second.co.id/product/${item.kode_extend || item.artikel}_1.JPG`}
                          alt={item.artikel}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null; 
                            e.target.src = "https://placehold.co/150x150?text=No+Image";
                          }}
                        />
                        {isBrokenSize && (
                          <span className="absolute top-0 right-0 flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                          </span>
                        )}
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">{item.artikel}</div>
                        <div className="text-[10px] text-slate-400 font-mono mt-0.5">{item.barcode}</div>
                      </div>
                    </div>
                  </td>

                  {/* CELL RATING DENGAN TOOLTIP LIST KOMENTAR */}
                  <td className="p-3 text-center">
                    <div className="relative inline-block group">
                      {/* Trigger Area (Bintang) */}
                      <div className="flex items-center justify-center gap-0.5 cursor-pointer p-1 rounded-md hover:bg-slate-100 transition-colors">
                        {[1, 2, 3, 4, 5].map((starIdx) => (
                          <Star
                            key={starIdx}
                            size={11}
                            className={
                              starIdx <= currentRating 
                                ? "text-amber-400 fill-amber-400" 
                                : "text-slate-200 fill-slate-100"
                            }
                          />
                        ))}
                        <span className="text-[10px] text-slate-400 ml-1 font-medium">({sampleComments.length})</span>
                      </div>

                      {/* Tooltip Card Panel */}
                      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-white border border-slate-200 rounded-xl shadow-xl p-3 z-50 pointer-events-none opacity-0 scale-95 origin-bottom group-hover:opacity-100 group-hover:scale-100 group-focus-within:opacity-100 group-focus-within:scale-100 transition-all duration-150">
                        <div className="flex items-center justify-between border-b border-slate-100 pb-2 mb-2">
                          <span className="font-semibold text-slate-800 text-[11px]">Ulasan Terbaru</span>
                          <span className="bg-amber-50 text-amber-700 font-bold px-1.5 py-0.5 rounded text-[10px] border border-amber-100 flex items-center gap-0.5">
                            ★ {currentRating}.0
                          </span>
                        </div>
                        
                        <div className="space-y-2 max-h-36 overflow-y-auto pr-0.5 scrollbar-thin">
                          {sampleComments.map((comment, cIdx) => (
                            <div key={cIdx} className="text-left bg-slate-50/60 p-2 rounded-lg border border-slate-100">
                              <div className="flex justify-between items-center text-[10px] font-medium mb-0.5">
                                <span className="text-slate-700 font-semibold">{comment.user}</span>
                                <span className="text-slate-400 font-normal">{comment.date}</span>
                              </div>
                              <p className="text-slate-600 text-[10px] leading-relaxed whitespace-normal break-words">
                                "{comment.text}"
                              </p>
                            </div>
                          ))}
                        </div>
                        
                        {/* Segitiga Panah Tooltip */}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-white border-r border-b border-slate-200 rotate-45 -mt-1 shadow-sm"></div>
                      </div>
                    </div>
                  </td>

                  <td className="p-3 text-slate-500">{item.dept}</td>
                  <td className="p-3 text-slate-500 max-w-[120px] truncate">{item.prinsipal}</td>
                  <td className="p-3 text-slate-600">
                    {item.gender || "-"} / <span className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-800 text-[10px] font-medium">{item.ukuran}</span>
                  </td>
                  
                  {/* ISI DATA MATRIX STATUS & BROKEN SIZE BADGE */}
                  <td className="p-3 text-center">
                    {isBrokenSize ? (
                      <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-amber-200">
                        ⚠️ BROKEN SIZE
                      </span>
                    ) : isStokAktif ? (
                      <span className="bg-emerald-50 text-emerald-700 text-[10px] font-medium px-2 py-0.5 rounded-full border border-emerald-100">
                        {uniqueSizesWithStock} Sizes Ready
                      </span>
                    ) : (
                      <span className="bg-slate-100 text-slate-400 text-[10px] rounded-full px-2 py-0.5">
                        Out of Stock
                      </span>
                    )}
                  </td>
                  
                  {/* ISI DATA UTAMA */}
                  <td className="p-3 text-center bg-slate-50/50 border-x border-slate-200/60 text-slate-800 font-medium">
                    {Number(item.stok_audit || 0).toLocaleString("id-ID")}
                  </td>
                  <td className="p-3 text-center bg-emerald-50/30 text-emerald-600 border-r border-slate-200/40">
                    {totalIn > 0 ? totalIn.toLocaleString("id-ID") : "-"}
                  </td>
                  <td className="p-3 text-center bg-rose-50/30 text-rose-600 border-r border-slate-200/40">
                    {totalOut > 0 ? totalOut.toLocaleString("id-ID") : "-"}
                  </td>
                  <td className="p-3 text-center bg-indigo-50/30 text-indigo-600 border-r border-slate-200 font-medium">
                    {stokAwal.toLocaleString("id-ID")}
                  </td>

                  {/* ISI DATA TODAY */}
                  <td className="p-3 text-center bg-emerald-100/30 text-emerald-700 border-r border-slate-200/40 font-medium">
                    {totalInToday > 0 ? `+${totalInToday.toLocaleString("id-ID")}` : "-"}
                  </td>
                  <td className="p-3 text-center bg-rose-100/30 text-rose-700 border-r border-slate-200/40 font-medium">
                    {totalOutToday > 0 ? `-${totalOutToday.toLocaleString("id-ID")}` : "-"}
                  </td>
                  
                  {/* STOK AKHIR */}
                  <td className={`p-3 text-center font-semibold ${isBrokenSize ? "bg-amber-100/40 text-amber-900 border-l border-amber-200" : "bg-violet-50 text-violet-700"}`}>
                    {Number(item.stok_akhir || 0).toLocaleString("id-ID")}
                  </td>
                </motion.tr>
              );
            })
          ) : (
            <tr key="empty">
              <td colSpan="13" className="p-12 text-center text-slate-400 bg-white">
                <div className="flex flex-col items-center justify-center gap-2">
                  <span className="text-xl">🔍</span>
                  <p className="text-slate-400">Data rincian artikel tidak ditemukan atau kosong.</p>
                </div>
              </td>
            </tr>
          )}
        </AnimatePresence>
      </tbody>
    </table>
  </div>

  {/* ========================================================= */}
  {/* FITUR: FLOATING ACTION BUTTON CHART MATRIX (TEMA INDIGO/BLUE) */}
  {/* ========================================================= */}
  <div className="fixed bottom-6 right-6 z-40 flex flex-col gap-2">
    <button
      onClick={() => setShowChartModal(true)}
      className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-medium px-4 py-3 rounded-full shadow-lg hover:from-indigo-700 hover:to-blue-700 transition-all active:scale-95 text-xs"
    >
      <LayoutGrid size={15} />
      Matrix Broken Size
    </button>
  </div>

  {/* MODAL WINDOW POPUP */}
  <AnimatePresence>
    {showChartModal && (
      <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-xs z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.98, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.98, y: 10 }}
          className="bg-white rounded-2xl w-full max-w-2xl shadow-xl overflow-hidden border border-slate-200/60"
        >
          {/* Header Modal - Soft & Clean Style */}
          <div className="p-4 bg-slate-50 border-b border-slate-200/80 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-blue-50 text-blue-500 rounded-lg">
                <Layers size={15} />
              </div>
              <div>
                <h4 className="text-xs font-semibold text-slate-800">Analisis Matriks Broken Size</h4>
                <p className="text-[10px] text-slate-400 font-normal">Pemantauan artikel kritis bersisa satu ukuran aktif</p>
              </div>
            </div>
            <button 
              onClick={() => setShowChartModal(false)}
              className="text-slate-400 hover:text-slate-600 transition-colors text-sm p-1 rounded-lg hover:bg-slate-100"
            >
              ✕
            </button>
          </div>

          {/* Isi Konten / Panel Analitis */}
          <div className="p-5 space-y-5">
            
            {/* Tambahan Widget Mini-KPI untuk Memperkuat Nuansa Analisis */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-slate-50/50 rounded-xl border border-slate-200/50">
                <span className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">Total Produk Terdampak</span>
                <div className="text-base font-bold text-slate-800 mt-0.5">
                  {(brokenSizeMatricesData[activeMatrixTab]?.reduce((acc, curr) => acc + curr.stokAkhir, 0) || 0).toLocaleString("id-ID")}
                  <span className="text-[10px] text-slate-400 font-normal ml-1">pcs</span>
                </div>
              </div>
              <div className="p-3 bg-slate-50/50 rounded-xl border border-slate-200/50">
                <span className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">Kelompok Terbanyak</span>
                <div className="text-base font-bold text-blue-600 truncate mt-0.5">
                  {brokenSizeMatricesData[activeMatrixTab]?.length > 0 
                    ? [...brokenSizeMatricesData[activeMatrixTab]].sort((a, b) => b.stokAkhir - a.stokAkhir)[0]?.label 
                    : "-"}
                </div>
              </div>
            </div>

            {/* Area Utama Grafik */}
            <div className="w-full h-52 text-xs">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={brokenSizeMatricesData[activeMatrixTab] || []} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis 
                    dataKey="label" 
                    fontSize={10} 
                    stroke="#94a3b8" 
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis 
                    fontSize={10} 
                    stroke="#94a3b8" 
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(val) => val.toLocaleString("id-ID")} 
                  />
                  
                  {/* Custom Tooltip dengan Gaya Dashboard Finansial */}
                  <Tooltip 
                    cursor={{ fill: '#f8fafc' }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        const totalStok = Number(data.stokAkhir || 0);
                        const totalAudit = Number(data.stokAudit || 0);
                        const persentaseSisa = totalAudit > 0 ? ((totalStok / totalAudit) * 100).toFixed(1) : "0.0";

                        return (
                          <div className="bg-white/95 backdrop-blur-xs p-3 rounded-xl border border-slate-200 shadow-lg text-[11px] space-y-1 font-normal text-slate-600 min-w-[160px]">
                            <p className="font-semibold text-slate-800 border-b border-slate-100 pb-1 mb-1 text-xs">{data.label}</p>
                            <div className="flex justify-between gap-4">
                              <span>Sisa Stok BS:</span>
                              <span className="font-semibold text-slate-900">{totalStok.toLocaleString("id-ID")} pcs</span>
                            </div>
                            <div className="flex justify-between gap-4">
                              <span>Rasio Sisa:</span>
                              <span className="font-semibold text-blue-500">{persentaseSisa}%</span>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  
                  {/* Bar Berwarna Biru Soft (Soft Sky Blue) */}
                  <Bar 
                    dataKey="stokAkhir" 
                    fill="#93c5fd" 
                    activeBar={{ fill: '#60a5fa' }}
                    radius={[4, 4, 0, 0]} 
                    maxBarSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Footer Modal */}
          <div className="p-3 bg-slate-50/80 border-t border-slate-200/80 flex items-center justify-between">
            {/* Switcher Dimensi Data */}
            <div className="flex gap-1 bg-slate-200/60 p-0.5 rounded-lg text-[10px]">
              {["dept", "brand", "size"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveMatrixTab(tab)}
                  className={`px-2.5 py-1 rounded-md transition-all ${activeMatrixTab === tab ? "bg-white text-slate-800 font-semibold shadow-xs" : "text-slate-500 hover:text-slate-700"}`}
                >
                  {tab.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Tombol Export Excel */}
            <button
              onClick={() => handleCardExportBroken("broken-size")}
              className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-900 text-white px-3 py-1.5 rounded-lg transition-all text-[11px] font-medium shadow-xs"
            >
              <Download size={12} />
              Unduh File Excel
            </button>
          </div>
        </motion.div>
      </div>
    )}
  </AnimatePresence>

  {/* Panel Footer Pagination */}
  {totalPages > 1 && (
    <div className="p-4 bg-slate-50/50 border-t border-slate-200 flex flex-col sm:flex-row gap-3 items-center justify-between text-xs text-slate-600">
      <div>
        Menampilkan <span className="text-slate-800 font-semibold">{Math.min(filteredArtikel.length, (currentPage - 1) * itemsPerPage + 1)}</span> - <span className="text-slate-800 font-semibold">{Math.min(filteredArtikel.length, currentPage * itemsPerPage)}</span> dari <span className="text-slate-800 font-semibold">{filteredArtikel.length}</span> Artikel
      </div>
      
      <div className="flex items-center gap-2">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="w-8 h-8 rounded-xl border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50 disabled:opacity-40 transition-all shadow-sm active:scale-95"
        >
          <ChevronLeft size={14} />
        </button>
        <div className="px-2">Halaman <span className="text-slate-800 font-semibold">{currentPage}</span> dari <span className="text-slate-800 font-semibold">{totalPages}</span></div>
        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="w-8 h-8 rounded-xl border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50 disabled:opacity-40 transition-all shadow-sm active:scale-95"
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  )}
</div>
    </div>
  );
}